/**
 * 面談・研修の記録の担当者名をまとめて変える(2026-10-09 発注者指示)。
 * 今の名前を選ぶ → 新しい名前を入れる → 件数を確かめて「変更する」。取込で入った名前(ログイン名など)を直すときに使う。
 */
import { useMemo, useState } from 'react';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { formatCount } from '@core/utils/formatters';
import type { TrainingRecord } from './training';
import { renameTrainer } from './trainingRepo';

export default function TrainingTrainerRenameModal({ records, userId, onClose, onDone }: { records: TrainingRecord[]; userId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const names = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of records) m.set(r.trainer, (m.get(r.trainer) ?? 0) + 1);
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'ja'));
  }, [records]);
  const [from, setFrom] = useState(names[0]?.[0] ?? '');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const count = names.find(([n]) => n === from)?.[1] ?? 0;
  const next = to.trim();

  async function run() {
    if (!next || next === from || count === 0) return;
    setBusy(true);
    try {
      const n = await renameTrainer(records, from, next, userId);
      toast(`担当者名を ${formatCount(n)}件 変更しました`);
      onDone();
    } catch (e) {
      toast(e instanceof Error ? `変更できませんでした: ${e.message}` : '変更できませんでした');
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(520px, 94vw)">
      <h3 className="training-modal-title">担当者名をまとめて変更</h3>
      {names.length === 0 ? (
        <p className="ui-hint">面談・研修・指導の記録がありません</p>
      ) : (
        <div className="training-form">
          <label className="training-field is-wide">
            <span>今の担当者名</span>
            <select className="ui-input" value={from} onChange={(e) => setFrom(e.target.value)} disabled={busy}>
              {names.map(([n, c]) => (
                <option key={n} value={n}>
                  {n || '(空欄)'}({formatCount(c)}件)
                </option>
              ))}
            </select>
          </label>
          <label className="training-field is-wide">
            <span>新しい担当者名</span>
            <input className="ui-input" value={to} onChange={(e) => setTo(e.target.value)} disabled={busy} />
          </label>
        </div>
      )}
      <div className="training-actions">
        <button type="button" className="ui-btn ui-btn-primary" onClick={() => void run()} disabled={busy || !next || next === from || count === 0}>
          {busy ? '変更中…' : `${formatCount(count)}件 変更する`}
        </button>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose} disabled={busy}>
          やめる
        </button>
      </div>
    </Modal>
  );
}
